import { useState, useEffect, useCallback } from 'react'
import { Loader2, Plus, Copy, Trash2, QrCode, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthContext } from '../contexts/AuthContext'
import registrationService, { RegistrationCode } from '../services/registrationService'
import clientService, { Client, Location } from '../services/clientService'
import { toast } from 'sonner'

export default function RegistrationCodes() {
  const { hasAnyGroup, user } = useAuthContext()
  const [open, setOpen] = useState(false)
  const [expirationHours, setExpirationHours] = useState(24)
  const [deviceName, setDeviceName] = useState('')
  const [selectedClientId, setSelectedClientId] = useState('')
  const [selectedLocationId, setSelectedLocationId] = useState('none')
  const [registrationCodes, setRegistrationCodes] = useState<RegistrationCode[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [formErrors, setFormErrors] = useState<{ clientId?: string }>({})
  
  const isSuperAdmin = hasAnyGroup(['superAdmin'])
  const isGroupAdmin = hasAnyGroup(['groupAdmin'])
  const userClientId = user?.userId || ''

  const loadRegistrationCodes = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const codes = await registrationService.getRegistrationCodes()
      setRegistrationCodes(codes)
      
      if (isSuperAdmin) {
        const clientsData = await clientService.listClients()
        setClients(clientsData)
      }
    } catch (err) {
      console.error('Error loading registration codes:', err)
      setError('Failed to load registration codes. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [isSuperAdmin])

  useEffect(() => {
    loadRegistrationCodes()
  }, [loadRegistrationCodes])

  useEffect(() => {
    const loadLocations = async () => {
      if (selectedClientId) {
        try {
          const locationsData = await clientService.listClientLocations(selectedClientId)
          setLocations(locationsData)
        } catch (err) {
          console.error('Error loading locations:', err)
        }
      } else {
        setLocations([])
        setSelectedLocationId('none')
      }
    }
    
    loadLocations()
  }, [selectedClientId])

  const handleGenerateCode = () => {
    setDeviceName('')
    setExpirationHours(24)
    setSelectedLocationId('none')
    setFormErrors({})
    
    if (isSuperAdmin) {
      setSelectedClientId('')
    } else if (isGroupAdmin) {
      setSelectedClientId(userClientId)
    }
    
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setDeviceName('')
    setExpirationHours(24)
    setSelectedClientId('')
    setSelectedLocationId('none')
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {}
    
    if (!selectedClientId) {
      errors.clientId = 'Client is required'
    }
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCreateCode = async () => {
    if (!validateForm()) {
      return
    }

    try {
      setCreating(true)
      const newCode = await registrationService.generateRegistrationCode({
        deviceName: deviceName.trim() || undefined,
        expirationHours,
        clientId: selectedClientId,
        locationId: selectedLocationId && selectedLocationId !== 'none' ? selectedLocationId : undefined,
      })
      
      setRegistrationCodes(prev => [newCode, ...prev])
      toast.success('Registration code generated successfully!')
      handleClose()
    } catch (err) {
      console.error('Error generating registration code:', err)
      setError('Failed to generate registration code. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      toast.success('Code copied to clipboard!')
    } catch (err) {
      console.error('Error copying code:', err)
      toast.error('Failed to copy code')
    }
  }

  const handleDeleteCode = async (codeId: string) => {
    try {
      await registrationService.deleteRegistrationCode(codeId)
      setRegistrationCodes(prev => prev.filter(code => code.id !== codeId))
      toast.success('Registration code deleted successfully!')
    } catch (err) {
      console.error('Error deleting registration code:', err)
      setError('Failed to delete registration code. Please try again.')
    }
  }

  const getClientName = (clientId?: string) => {
    if (!clientId) return '-'
    const client = clients.find(c => c.id === clientId)
    return client?.name || clientId
  }

  const getLocationName = (locationId?: string) => {
    if (!locationId) return '-'
    const location = locations.find(l => l.id === locationId)
    return location?.name || locationId
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  const getStatusVariant = (code: RegistrationCode) => {
    if (code.isUsed) return 'default'
    if (code.expiresAt && new Date(code.expiresAt) < new Date()) return 'destructive'
    return 'secondary'
  }

  const getStatusLabel = (code: RegistrationCode) => {
    if (code.isUsed) return 'Used'
    if (code.expiresAt && new Date(code.expiresAt) < new Date()) return 'Expired'
    return 'Active'
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold">Registration Codes</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadRegistrationCodes}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={handleGenerateCode}>
            <Plus className="mr-2 h-4 w-4" />
            Generate Code
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Device Name</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {registrationCodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <QrCode className="h-12 w-12 text-muted-foreground" />
                    <div>
                      <h3 className="text-lg font-semibold mb-1">No registration codes generated</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Generate your first registration code to start adding devices.
                      </p>
                      <Button onClick={handleGenerateCode}>
                        <Plus className="mr-2 h-4 w-4" />
                        Generate First Code
                      </Button>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              registrationCodes.map((code) => (
                <TableRow key={code.id}>
                  <TableCell>
                    <code className="font-mono font-bold text-sm">{code.code}</code>
                  </TableCell>
                  <TableCell>{code.deviceName || '-'}</TableCell>
                  <TableCell>{getClientName(code.clientId)}</TableCell>
                  <TableCell>{getLocationName(code.locationId)}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusVariant(code)}>
                      {getStatusLabel(code)}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(code.createdAt)}</TableCell>
                  <TableCell>{formatDate(code.expiresAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyCode(code.code)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    {!code.isUsed && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCode(code.id)}
                      >
                        <Trash2 stroke="red" className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Generate Code Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Generate Registration Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {isSuperAdmin && (
              <div className="space-y-2">
                <Label htmlFor="client">Client *</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger id="client" className={formErrors.clientId ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.length === 0 ? (
                      <SelectItem value="none" disabled>No clients available</SelectItem>
                    ) : (
                      clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {formErrors.clientId && (
                  <p className="text-sm text-destructive">{formErrors.clientId}</p>
                )}
                {!formErrors.clientId && (
                  <p className="text-sm text-muted-foreground">Device will be assigned to this client</p>
                )}
              </div>
            )}
            
            {isGroupAdmin && (
              <Alert>
                <AlertDescription>
                  This code will be assigned to your client automatically
                </AlertDescription>
              </Alert>
            )}
            
            {selectedClientId && (
              <div className="space-y-2">
                <Label htmlFor="location">Location (Optional)</Label>
                <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                  <SelectTrigger id="location">
                    <SelectValue placeholder="No specific location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific location</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Optionally assign device to a specific location
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="deviceName">Device Name (Optional)</Label>
              <Input
                id="deviceName"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g., Lobby Display"
              />
              <p className="text-sm text-muted-foreground">
                Optional name to identify the device this code is for
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="expiration">Expiration (hours)</Label>
              <Input
                id="expiration"
                type="number"
                min={1}
                max={168}
                value={expirationHours}
                onChange={(e) => setExpirationHours(Number(e.target.value))}
              />
              <p className="text-sm text-muted-foreground">
                Code will expire after the specified number of hours (1-168)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateCode} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Code'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
